import React, { Component, Fragment } from 'react';
import { times } from 'lodash';
import { groupByOverlap, gridTemplateRows } from './Calendar';
import { tenMinutes, hoursInDay, minutesInHour } from './Constants';
import { withStyles, WithStyles, Theme } from '@material-ui/core';
import PropTypes from 'prop-types';
import moment from 'moment';
import { compose } from 'recompose';
import {
  CalendarColumnProps,
  ClassNames,
  ColorScheme,
  Dimensions,
  GroupedEvent,
} from '../../../types/components/Calendar';

const toGridRow = (from: number, to: number): string => from + 1 + '/' + (to + 1);
const toMinutes = (hours: number): number => (hours * minutesInHour) / tenMinutes;

const defaultColorScheme: ColorScheme = {
  divider: '#e0e0e0',
  background: '#ffffff',
  hover: '#f5f5f5',
};

const defaultDimensions: Dimensions = {
  tenMinutesHeightB: 8,
  tenMinutesHeightM: 6,
  tenMinutesHeightS: 4,
  groupedEventsMargin: '0 2px',
};

const withDefaultSettings = <P extends { colorScheme?: any; dimensions?: any }>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const WithDefaultSettings: React.FC<P> = (props) => {
    const mergedProps = {
      ...props,
      colorScheme: {
        ...defaultColorScheme,
        ...(props.colorScheme || {}),
      },
      dimensions: {
        ...defaultDimensions,
        ...(props.dimensions || {}),
      },
    };

    return <Component {...(mergedProps as P)} />;
  };

  return WithDefaultSettings;
};

export const CalendarColumn = compose(
  withDefaultSettings,
  withStyles((theme: Theme) => ({
    root: {
      borderRight: ({ colorScheme }: CalendarColumnProps) => `1px solid ${colorScheme.divider}`,
      backgroundColor: ({ colorScheme }: CalendarColumnProps) => colorScheme.background,
      display: 'grid',
      overflow: 'hidden',
      gridTemplateRows: ({ startHour, endHour }: CalendarColumnProps) =>
        gridTemplateRows(hoursInDay, startHour, endHour, '1fr'),
    },
    timegrid: {
      display: 'grid',

      [theme.breakpoints.up('md')]: {
        gridTemplateRows: ({ startHour, endHour, dimensions }: CalendarColumnProps) =>
          gridTemplateRows(
            toMinutes(hoursInDay),
            toMinutes(startHour),
            toMinutes(endHour),
            `${dimensions.tenMinutesHeightB}px`
          ),
      },
      [theme.breakpoints.only('sm')]: {
        gridTemplateRows: ({ startHour, endHour, dimensions }: CalendarColumnProps) =>
          gridTemplateRows(
            toMinutes(hoursInDay),
            toMinutes(startHour),
            toMinutes(endHour),
            `${dimensions.tenMinutesHeightM}px`
          ),
      },
      [theme.breakpoints.only('xs')]: {
        gridTemplateRows: ({ startHour, endHour, dimensions }: CalendarColumnProps) =>
          gridTemplateRows(
            toMinutes(hoursInDay),
            toMinutes(startHour),
            toMinutes(endHour),
            `${dimensions.tenMinutesHeightS}px`
          ),
      },
      padding: 0,
      gridColumn: '1 / span 1',
      gridRow: '1 / span 24',
    },
    event: {
      border: ({ colorScheme }: CalendarColumnProps) => `1px solid ${colorScheme.divider}`,
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      wordBreak: 'break-all',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: ({ colorScheme }: CalendarColumnProps) => colorScheme.hover,
      zIndex: 2,
      '-webkit-transform': 'translate3d(0,0,0)',
    },
    hour: {
      display: 'grid',
      gridColumn: `1 / span 1`,
      gridTemplateRows: `repeat(${minutesInHour / tenMinutes}, 1fr)`,
    },
    tenMinutes: {
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      opacity: 0.5,
      '&:last-child': {
        opacity: 1,
      },
      '&:hover': {
        backgroundColor: ({ colorScheme }: CalendarColumnProps) => colorScheme.hover,
      },
    },
    separator: {
      backgroundColor: ({ colorScheme }: CalendarColumnProps) => colorScheme.divider,
      height: 1,
    },
  }))
)(
  class CalendarColumnComponent extends Component<CalendarColumnProps & WithStyles<ClassNames>, {}> {
    static propTypes = {
      startHour: PropTypes.number.isRequired,
      endHour: PropTypes.number.isRequired,
      date: PropTypes.object.isRequired,
      events: PropTypes.array.isRequired,
      renderEvent: PropTypes.func.isRequired,
      dimensions: PropTypes.object.isRequired,
      colorScheme: PropTypes.object.isRequired,
      onTimeSlotClick: PropTypes.func,
    };

    static defaultProps = {
      startHour: 0,
      endHour: 24,
      date: moment.utc(),
      events: [],
      renderEvent: (event: Event) => `${event.start.format('LT')}-${event.end.format('LT')} ${event.title}`,
    };

    HoursGrid = (): JSX.Element => {
      const { classes, date, startHour, endHour, onTimeSlotClick } = this.props;
      return (
        <Fragment>
          {times(hoursInDay, (hour: number) => (
            <div key={hour} className={classes.hour} style={{ gridRow: `${hour + 1} / span 1` }}>
              {hour >= startHour &&
                hour <= endHour &&
                times(minutesInHour / tenMinutes, (minutes: number) => (
                  <div
                    className={classes.tenMinutes}
                    key={minutes}
                    style={{ gridRow: `${minutes + 1} / span 1` }}
                    onClick={() => {
                      onTimeSlotClick &&
                        onTimeSlotClick(
                          date
                            .clone()
                            .hour(hour)
                            .minutes(minutes * tenMinutes)
                        );
                    }}
                  >
                    <div className={classes.separator} />
                  </div>
                ))}
            </div>
          ))}
        </Fragment>
      );
    };

    render(): JSX.Element {
      const { classes, events: allEvents, date, startHour, endHour, renderEvent, style, dimensions } = this.props;
      const { HoursGrid } = this;

      const groupedEvents: GroupedEvent[] = groupByOverlap(allEvents, date);

      return (
        <div className={classes.root} style={style}>
          <HoursGrid />
          <div className={classes.timegrid}>
            {groupedEvents.map(({ startRow, endRow, events }, i) => {
              const normalizedStartRow = Math.max(startRow, toMinutes(startHour));
              const normalizedEndRow = Math.min(endRow, toMinutes(endHour));
              return (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateRows: `repeat(${normalizedEndRow - normalizedStartRow}, 1fr
                    )`,
                    gridRow: toGridRow(normalizedStartRow, normalizedEndRow),
                    margin: dimensions.groupedEventsMargin,
                  }}
                >
                  {events.map((event, i) => (
                    <div
                      key={i}
                      className={classes.event}
                      style={{
                        gridRow: toGridRow(
                          Math.max(event.startRow, toMinutes(startHour)) - normalizedStartRow,
                          Math.min(event.endRow, toMinutes(endHour)) - normalizedStartRow
                        ),
                        borderRadius: '4px',
                      }}
                    >
                      {renderEvent(event, i)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
  }
);
